import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Household } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { isValidEmail } from '../../../../common/src/util.ts';
import { localStorageGet, LocalStorageKey, localStorageSet } from '../../data/client_local_storage.ts';
import { synchronizeOrgsWithServer } from '../../data/use_current_org.tsx';
import { getHouseholdFromServer } from '../../data/use_household.tsx';
import { alert } from '../../util/confirm.tsx';
import { Button } from './button.tsx';
import {
  getRedeemablePromoCampaignSlug,
  goToNextOnboardingStep,
  isAdminInvite,
  Message,
  TextInput,
} from './common.tsx';
import { Frame } from './frame.tsx';

export const HouseholdFind = () => {
  const emailAddressInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    emailAddressInputRef.current?.focus();
  }, [emailAddressInputRef]);

  const onContinue = useCallback(async () => {
    const householdEmail = emailAddressInputRef.current?.value;
    if (!householdEmail || !isValidEmail(householdEmail)) {
      await alert({ confirmation: <span>Please enter a valid email address</span> });
      return;
    }
    let household: Household | undefined = undefined;
    try {
      const householdToken = await server_api.getHouseholdToken({ householdEmail });
      if (householdToken) {
        household = await getHouseholdFromServer(householdToken);
      }
    } catch {
      await alert({
        confirmation: (
          <span>
            Network error retrieving household. Please make sure that you are connected to the Internet. If this problem
            persists, contact your internet service provider.
          </span>
        ),
      });
      return;
    }
    if (!household) {
      await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdFind, action: 'notFound' });
      return;
    }
    localStorageSet(LocalStorageKey.Household, household);
    await synchronizeOrgsWithServer({ force: true });
    await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdFind, action: 'found', household });
  }, [emailAddressInputRef, navigate]);

  const [isDisabled, setIsDisabled] = useState<boolean>(
    () => !localStorageGet(LocalStorageKey.OnboardingHouseholdEmail),
  );
  const onEmailChange = useCallback(() => {
    setIsDisabled(!isValidEmail(emailAddressInputRef.current?.value));
    localStorageSet(LocalStorageKey.OnboardingHouseholdEmail, emailAddressInputRef.current?.value);
  }, []);

  const prompt = isAdminInvite()
    ? 'What is your household email address?'
    : getRedeemablePromoCampaignSlug()
      ? // a promotional campaign's visitor may be signing in or starting from nothing (GitHub issue #3551)
        'Enter your email to sign in, or to create your household.'
      : 'What email address was used for the household?';

  return (
    <Frame
      onboardingPage={OnboardingPages.HouseholdFind}
      mainContent={
        <>
          <Message $widthInChars={18}>{prompt}</Message>
          <TextInput
            type="email"
            defaultValue={localStorageGet(LocalStorageKey.OnboardingHouseholdEmail)}
            onChange={onEmailChange}
            ref={emailAddressInputRef}
          />
        </>
      }
      footerContent={
        <Button onClick={onContinue} disabled={isDisabled}>
          CONTINUE
        </Button>
      }
      onEnterKeypress={onContinue}
      containsInputElement={true}
    />
  );
};
